import { createFileRoute } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { PageShell, PageHero } from "@/components/PageShell";
import { Reveal } from "@/components/Reveal";
import { LangLink as Link } from "@/components/LangLink";
import heroMissions from "@/assets/hero-missions.png";
import bf1Logo from "@/assets/missions/bf1.png";
import arionLogo from "@/assets/missions/arion.png";
import pururucaLogo from "@/assets/missions/pururuca.png";
import debeLogo from "@/assets/missions/debe.png";
import mlbrLogo from "@/assets/missions/mlbr.png";
import fogoLogo from "@/assets/missions/fogo.png";
import bebeLogo from "@/assets/missions/bebe.png";
import seguraPeaoLogo from "@/assets/missions/segura-peao.png";
import tremBaumLogo from "@/assets/missions/trem-baum.png";

export const Route = createFileRoute("/$lang/missions")({
  head: () => ({
    meta: [
      { title: "Missions — BIZU Space" },
      {
        name: "description",
        content:
          "BIZU Space missions — including the Brazilian Microlauncher (MLBR — Brazilian Microlauncher), funded by FINEP and AEB.",
      },
      { property: "og:title", content: "Missions — BIZU Space" },
      {
        property: "og:description",
        content: "BIZU Space missions including the Brazilian Microlauncher (MLBR).",
      },
      { property: "og:url", content: "/missions" },
    ],
    links: [{ rel: "canonical", href: "https://bizu.space/missions" }],
  }),
  component: Missions,
});

const missions: { id: string; year: string; logo: string; href?: string; status: "completed" | "inProgress" }[] = [
  {
    id: "bf1",
    year: "2022",
    logo: bf1Logo,
    href: "https://www.youtube.com/watch?v=nyOrN-O-XLY",
    status: "completed",
  },
  {
    id: "mlbr",
    year: "2024",
    logo: mlbrLogo,
    href: "/mlbr",
    status: "inProgress",
  },
  {
    id: "arion",
    year: "2024",
    logo: arionLogo,
    href: "/products/arion-liquid-rocket-engine",
    status: "inProgress",
  },
  {
    id: "fogo-no-parquinho",
    year: "2025",
    logo: fogoLogo,
    href: "https://www.youtube.com/shorts/WMyORh929_I",
    status: "completed",
  },
  {
    id: "pururuca",
    year: "2025",
    logo: pururucaLogo,
    href: "https://www.youtube.com/shorts/k8UgULBldDI",
    status: "completed",
  },
  {
    id: "bebe-conforto",
    year: "2025",
    logo: bebeLogo,
    href: "https://www.linkedin.com/posts/bizu-space_bizupod-brasil-BIZU POD-activity-7395160683476307968-ai8H/?utm_source=social_share_send&utm_medium=member_desktop_web&rcm=ACoAADBCuZQBN5JMOUdxOTa85iahtlqzOGocdOc",
    status: "completed",
  },
  {
    id: "debe",
    year: "2026",
    logo: debeLogo,
    href: "https://www.instagram.com/p/DUi5ltFgAbM/?utm_source=ig_web_copy_link&igsh=MzRlODBiNWFlZA%3D%3D",
    status: "completed",
  },
  {
    id: "segura-peao",
    year: "2026",
    logo: seguraPeaoLogo,
    status: "completed",
  },
  {
    id: "trem-baum",
    year: "2026",
    logo: tremBaumLogo,
    status: "inProgress",
  },
];

function Missions() {
  const { t } = useTranslation("missions");
  const sortedMissions = [...missions].sort((a, b) => parseInt(a.year) - parseInt(b.year));

  return (
    <PageShell>
      {/* HERO */}
      <PageHero
        title={t("hero.title")}
        description={t("hero.description")}
        image={heroMissions}
      />

      {/* TIMELINE */}
      <section className="relative container-bizu py-32 max-w-6xl overflow-hidden">
        <div className="absolute left-1/2 top-0 bottom-0 w-px bg-white/10 -translate-x-1/2 hidden lg:block" />
        <div className="relative space-y-24 lg:space-y-36">
          {sortedMissions.map((mission, idx) => {
            const isEven = idx % 2 === 0;
            const title = t(`items.${mission.id}.title`);
            const subtitle = t(`items.${mission.id}.subtitle`, { defaultValue: "" });
            const description = t(`items.${mission.id}.description`);
            const cta = t(`items.${mission.id}.cta`, { defaultValue: "" });

            const StatusBadge = () => (
              <div className={`mt-3 inline-flex items-center rounded-full px-3 py-1 text-[11px] uppercase tracking-wider font-bold ${
                mission.status === 'completed' 
                  ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20' 
                  : 'bg-amber-500/10 text-amber-400 border border-amber-500/20'
              }`}>
                {mission.status === 'completed' ? t("status.completed", "Completed") : t("status.inProgress", "In Progress")}
              </div>
            );

            return (
              <div
                key={`${mission.year}-${mission.id}`}
                id={mission.id}
                className="scroll-mt-32"
              >
                <Reveal effect="up">
                  {/* Mobile Layout (< lg) */}
                  <div className="flex flex-col items-center gap-6 lg:hidden text-center">
                    <div className="text-6xl font-black text-white/15 select-none tracking-tighter">
                      {mission.year}
                    </div>
                    <div className="w-48 h-48 flex items-center justify-center p-3 relative">
                      <img
                        src={mission.logo}
                        alt={title}
                        className="w-full h-full object-contain"
                        loading="lazy"
                      />
                    </div>
                    <div className="space-y-4">
                      <div>
                        <h2 className="display text-2xl font-bold text-white uppercase tracking-wider">
                          {title}
                        </h2>
                        {subtitle && (
                          <p className="text-sm text-white/50 italic mt-1">{subtitle}</p>
                        )}
                        <StatusBadge />
                      </div>
                      <p className="text-white/60 leading-relaxed text-sm max-w-md mx-auto">
                        {description}
                      </p>
                      {cta && mission.href && (
                        <Link
                          to={mission.href as any}
                          className="group inline-flex items-center gap-3 text-[15px] font-semibold uppercase tracking-[0.18em] text-white transition hover:text-accent"
                        >
                          {cta}

                          <span className="transition-transform duration-300 group-hover:translate-x-1">
                            →
                          </span>
                        </Link>
                      )}
                    </div>
                  </div>

                  {/* Desktop Layout (>= lg) */}
                  <div className="hidden lg:grid lg:grid-cols-[1fr_120px_1fr] gap-8 items-center">
                    {/* Left Column */}
                    {isEven ? (
                      // Text on the left
                      <div className="text-right space-y-4 pr-4">
                        <div>
                          <h2 className="display text-3xl font-extrabold text-white uppercase tracking-widest leading-tight">
                            {title}
                          </h2>
                          {subtitle && (
                            <p className="text-base text-white/50 italic mt-1">
                              {subtitle}
                            </p>
                          )}
                          <StatusBadge />
                        </div>
                        <p className="text-white/60 leading-relaxed text-base max-w-lg ml-auto">
                          {description}
                        </p>
                        {cta && mission.href && (
                          <Link
                            to={mission.href as any}
                            className="group inline-flex items-center gap-3 text-[15px] font-semibold uppercase tracking-[0.18em] text-white transition hover:text-accent ml-auto"
                          >
                            {cta}

                            <span className="transition-transform duration-300 group-hover:translate-x-1">
                              →
                            </span>
                          </Link>
                        )}
                      </div>
                    ) : (
                      // Year on the left
                      <div className="text-right text-8xl xl:text-9xl font-black text-white/15 select-none pr-4 tracking-tighter">
                        {mission.year}
                      </div>
                    )}

                    {/* Center Column (Badge / Logo) */}
                    <div className="flex flex-col items-center justify-center relative z-10">
                      <div className="w-48 h-48 rounded-full  flex items-center justify-center  p-3 shadow-2xl relative group hover:border-accent transition duration-500">
                        <img
                          src={mission.logo}
                          alt={title}
                          className="w-full h-full object-contain transition duration-500 group-hover:scale-110"
                          loading="lazy"
                        />
                      </div>
                    </div>

                    {/* Right Column */}
                    {isEven ? (
                      // Year on the right
                      <div className="text-left text-8xl xl:text-9xl font-black text-white/15 select-none pl-4 tracking-tighter">
                        {mission.year}
                      </div>
                    ) : (
                      // Text on the right
                      <div className="text-left space-y-4 pl-4">
                        <div>
                          <h2 className="display text-3xl font-extrabold text-white uppercase tracking-widest leading-tight">
                            {title}
                          </h2>
                          {subtitle && (
                            <p className="text-base text-white/50 italic mt-1">
                              {subtitle}
                            </p>
                          )}
                          <StatusBadge />
                        </div>
                        <p className="text-white/60 leading-relaxed text-base max-w-lg">
                          {description}
                        </p>
                        {cta && mission.href && (
                          <Link
                            to={mission.href as any}
                            className="group inline-flex items-center gap-3 text-[15px] font-semibold uppercase tracking-[0.18em] text-white transition hover:text-accent"
                          >
                            {cta}

                            <span className="transition-transform duration-300 group-hover:translate-x-1">
                              →
                            </span>
                          </Link>
                        )}
                      </div>
                    )}
                  </div>
                </Reveal>
              </div>
            );
          })}
        </div>
      </section>
    </PageShell>
  );
}
